/** <scapescene-film-screen> — the stitched narrated film, and the
 * projector view: the film-negative strip up top, the film (or its
 * current status) below, then the large per-frame renders, with a
 * footer action to combine the scene videos (+ narration captions),
 * or combine them again. */

import { ApiError, assetKey } from "../data/client";
import { filmDownloadUrl } from "../data/api";
import { router } from "../control/router";
import { store } from "../store";
import type { ApiFrame } from "../data/types";
import { frameCount } from "../data/definitions";
import { t, tn } from "../lib/i18n";

import "../components/scapescene-filmstrip";
import "../components/scapescene-footer-bar";
import "../components/scapescene-player";

class WorkshopFilmScreen extends HTMLElement {
  #unsubscribe: (() => void) | null = null;
  #busy = false;
  #actionError = "";

  // Cached refs so re-renders can patch in place instead of recreating
  // media elements (which would restart playback / flash thumbnails).
  #renderTiles: HTMLElement[] = [];
  #introTile: HTMLElement | null = null;
  #outroTile: HTMLElement | null = null;
  #player: HTMLElement | null = null;
  #footer: HTMLElement | null = null;

  connectedCallback(): void {
    this.#render();
    this.#unsubscribe = store.onChange(() => this.#update());
    store.startPolling();
  }

  disconnectedCallback(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  #render(): void {
    const group = store.group;

    this.innerHTML = "";
    this.className = "";

    const strip = document.createElement("scapescene-filmstrip");
    strip.setAttribute("position", "film");
    strip.addEventListener("frame-change", (event) => {
      const detail = (event as CustomEvent<{ position: number }>).detail;
      router.navigate(`/frame/${detail.position}`);
    });
    strip.addEventListener("setup-open", () => router.navigate("/group"));
    this.appendChild(strip);

    const screen = document.createElement("div");
    screen.className = "screen";
    this.appendChild(screen);

    const heading = document.createElement("h1");
    heading.textContent = group?.projectTitle || t("theFilm");
    screen.appendChild(heading);

    const sub = document.createElement("p");
    sub.className = "hint film-subline";
    screen.appendChild(sub);

    const card = document.createElement("div");
    card.className = "card film-area";
    screen.appendChild(card);

    const rendersTitle = document.createElement("h2");
    rendersTitle.className = "film-renders-title";
    rendersTitle.textContent = t("theRenders");
    rendersTitle.hidden = true;
    screen.appendChild(rendersTitle);

    const renders = document.createElement("div");
    renders.className = "film-renders";
    this.#introTile = this.#buildRenderTile();
    renders.appendChild(this.#introTile);
    this.#renderTiles = [];
    for (
      let position = 1;
      position <= frameCount(store.definitions);
      position += 1
    ) {
      const tile = this.#buildRenderTile();
      this.#renderTiles.push(tile);
      renders.appendChild(tile);
    }
    this.#outroTile = this.#buildRenderTile();
    renders.appendChild(this.#outroTile);
    screen.appendChild(renders);

    const footer = document.createElement("scapescene-footer-bar");
    footer.addEventListener("footer-primary", () => void this.#onRender());
    screen.appendChild(footer);
    this.#footer = footer;

    this.#update();
  }

  #buildRenderTile(): HTMLElement {
    const tile = document.createElement("figure");
    tile.className = "film-render";
    tile.hidden = true;

    const visual = document.createElement("div");
    visual.className = "film-render-visual";
    tile.appendChild(visual);

    const caption = document.createElement("figcaption");
    caption.className = "hint film-render-caption";
    tile.appendChild(caption);

    return tile;
  }

  #update(): void {
    const group = store.group;

    // Definitions may land after the first build; rebuild the tile row
    // once the real frame count is known (at most once per page load).
    if (
      store.definitions &&
      this.#renderTiles.length !== frameCount(store.definitions)
    ) {
      this.#render();
      return;
    }

    const sub = this.querySelector<HTMLElement>(".film-subline");
    if (sub) {
      sub.textContent = group
        ? `${group.name || t("groupFallback")} · ${group.code}`
        : "";
    }

    this.#updateFilmArea(group);
    this.#updateRenders(group);
    this.#updateFooter(group);
  }

  /** The large renders: the intro card (ready as soon as the group is
   * set up), one tile per frame that has a render, and the credits
   * card. */
  #updateRenders(group: typeof store.group): void {
    let any = this.#updateCardTile(
      this.#introTile, group?.introUrl || "", t("introCard"),
    );
    for (let index = 0; index < this.#renderTiles.length; index += 1) {
      const frame = group?.frames.find((f) => f.position === index + 1) ?? null;
      any = this.#updateRenderTile(this.#renderTiles[index], frame) || any;
    }
    any = this.#updateCardTile(
      this.#outroTile, group?.outroUrl || "", t("creditsCard"),
    ) || any;
    const title = this.querySelector<HTMLElement>(".film-renders-title");
    if (title) title.hidden = !any;
  }

  /** An intro/credits card tile — a pre-rendered film segment that plays
   * on its own long before the film exists. */
  #updateCardTile(tile: HTMLElement | null, src: string, label: string): boolean {
    if (!tile) return false;
    tile.hidden = !src;
    if (!src) return false;

    const caption = tile.querySelector<HTMLElement>(".film-render-caption");
    if (caption) caption.textContent = label;

    const visual = tile.querySelector<HTMLElement>(".film-render-visual");
    if (!visual) return true;
    if (visual.dataset.kind === "card" && visual.dataset.src === assetKey(src)) return true;
    visual.dataset.kind = "card";
    visual.dataset.src = assetKey(src);
    visual.innerHTML = "";

    const video = document.createElement("video");
    video.className = "film-render-media";
    video.src = src;
    video.controls = true;
    video.muted = true;
    video.loop = true;
    video.autoplay = true;
    video.playsInline = true;
    video.preload = "metadata";
    visual.appendChild(video);
    return true;
  }

  #updateRenderTile(tile: HTMLElement, frame: ApiFrame | null): boolean {
    const videoUrl = frame?.videoUrl || "";
    const stillUrl = frame?.stillUrl || "";
    const kind = videoUrl ? "video" : stillUrl ? "still" : "empty";
    const src = videoUrl || stillUrl;

    tile.hidden = kind === "empty";
    if (kind === "empty") return false;

    const caption = tile.querySelector<HTMLElement>(".film-render-caption");
    if (caption && frame) {
      caption.textContent = `${t("frameWord")} ${frame.position} — ${kind === "video" ? t("clipWord") : t("pictureWord")}`;
    }

    const visual = tile.querySelector<HTMLElement>(".film-render-visual");
    if (!visual) return true;
    if (visual.dataset.kind === kind && visual.dataset.src === assetKey(src)) {
      // Unchanged — leave the playing video / loaded image alone.
      return true;
    }
    visual.dataset.kind = kind;
    visual.dataset.src = assetKey(src);
    visual.innerHTML = "";

    if (kind === "video") {
      const video = document.createElement("video");
      video.className = "film-render-media";
      video.src = src;
      video.controls = true;
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = "metadata";
      visual.appendChild(video);
    } else {
      const img = document.createElement("img");
      img.className = "film-render-media";
      img.src = src;
      img.alt = frame ? tn("frameRenderAlt", frame.position) : "";
      visual.appendChild(img);
    }
    return true;
  }

  #updateFilmArea(group: typeof store.group): void {
    const area = this.querySelector<HTMLElement>(".film-area");
    if (!area) return;

    const g = group;
    const status = g?.filmStatus ?? "idle";
    const stitchedUrl = g?.stitchedUrl ?? "";

    // Figure out what the area should currently contain and only rebuild
    // when that description changes, so a playing <scapescene-player>
    // survives unrelated polling updates.
    const desired =
      g?.videoEnabled === false
        ? "disabled"
        : status === "error"
          ? "error"
          : status === "rendering"
            ? "rendering"
            : stitchedUrl
              ? "ready"
              : "empty";

    if (area.dataset.state === desired) {
      // Same shape as before — patch details in place instead of
      // rebuilding, so a playing <scapescene-player> is untouched.
      if (desired === "ready" && this.#player) {
        // Same film, new signature on every poll: only swap the source
        // when the file itself changed (a re-rendered film).
        if (
          assetKey(this.#player.getAttribute("src") ?? "") !==
          assetKey(stitchedUrl)
        ) {
          this.#player.setAttribute("src", stitchedUrl);
        }
        this.#player.setAttribute("subtitles", g?.subtitlesUrl ?? "");
        const link = area.querySelector<HTMLAnchorElement>(".film-download");
        if (link && g) link.href = filmDownloadUrl(g.code);
      } else if (desired === "error") {
        const banner = area.querySelector<HTMLElement>(".error-banner");
        if (banner) {
          banner.textContent = g?.filmError || t("filmFailed");
        }
      }
      return;
    }

    area.dataset.state = desired;
    area.innerHTML = "";
    this.#player = null;

    if (desired === "disabled") {
      const hint = document.createElement("p");
      hint.className = "hint";
      hint.textContent = t("videoNotEnabledWorkshop");
      area.appendChild(hint);
      return;
    }

    if (desired === "rendering") {
      const busy = document.createElement("p");
      busy.className = "hint film-busy";
      busy.textContent = t("renderingFilm");
      area.appendChild(busy);
      return;
    }

    if (desired === "error") {
      const banner = document.createElement("div");
      banner.className = "error-banner";
      banner.textContent = g?.filmError || t("filmFailed");
      area.appendChild(banner);
      return;
    }

    if (desired === "ready") {
      const player = document.createElement("scapescene-player");
      player.setAttribute("src", stitchedUrl);
      player.setAttribute("subtitles", g?.subtitlesUrl ?? "");
      area.appendChild(player);
      this.#player = player;

      const actions = document.createElement("div");
      actions.className = "film-actions";

      const link = document.createElement("a");
      link.className = "film-download hint";
      // The API download endpoint, not the media URL: a cross-origin
      // media link ignores the download attribute and just navigates.
      link.href = g ? filmDownloadUrl(g.code) : stitchedUrl;
      link.textContent = t("downloadFilm");
      actions.appendChild(link);

      const finalView = document.createElement("a");
      finalView.className = "film-final-link hint";
      finalView.href = "#/final";
      finalView.textContent = t("watchFullScreen");
      actions.appendChild(finalView);

      area.appendChild(actions);
      return;
    }

    const empty = document.createElement("p");
    empty.className = "hint";
    empty.textContent = t("noFilmYet");
    area.appendChild(empty);
  }

  #updateFooter(group: typeof store.group): void {
    const footer = this.#footer;
    if (!footer) return;

    const g = group;
    const hasAnyVideo = !!g?.frames.some((f) => !!f.videoUrl);
    const rendering = g?.filmStatus === "rendering";
    const videoDisabled = g?.videoEnabled === false;

    footer.setAttribute(
      "primary-label",
      g?.stitchedUrl ? t("reRenderFilm") : t("renderFilm"),
    );

    let disabledReason = "";
    if (videoDisabled) {
      disabledReason = t("videoNotEnabledShort");
    } else if (!hasAnyVideo) {
      disabledReason = t("renderOneFrameFirst");
    } else if (rendering) {
      disabledReason = t("renderingFilmShort");
    }

    if (disabledReason || this.#busy) {
      footer.setAttribute("primary-disabled", "true");
    } else {
      footer.removeAttribute("primary-disabled");
    }

    if (this.#busy || rendering) {
      footer.setAttribute("primary-busy", "true");
    } else {
      footer.removeAttribute("primary-busy");
    }

    const hint = this.#actionError || disabledReason || t("combineHint");
    if (hint) {
      footer.setAttribute("primary-hint", hint);
    } else {
      footer.removeAttribute("primary-hint");
    }
  }

  async #onRender(): Promise<void> {
    if (this.#busy) return;
    const g = store.group;
    if (!g) return;
    const hasAnyVideo = g.frames.some((f) => !!f.videoUrl);
    if (g.videoEnabled === false || !hasAnyVideo || g.filmStatus === "rendering") return;

    this.#busy = true;
    this.#actionError = "";
    this.#update();
    try {
      await store.renderFilm();
    } catch (error) {
      this.#actionError = error instanceof ApiError ? error.detail : t("couldNotRenderFilm");
    } finally {
      this.#busy = false;
      this.#update();
    }
  }
}

if (!customElements.get("scapescene-film-screen")) {
  customElements.define("scapescene-film-screen", WorkshopFilmScreen);
}

export {};
